import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Flex, Checkbox, Alert } from "antd"
import { ClockCircleOutlined } from "@ant-design/icons"
import SignatureCanvas from "react-signature-canvas"
import styles from "./index.module.css"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { useTranslation } from "react-i18next"
import { updateWaiverSubmission, getWaiverSubmissionsByTripBooking, saveWaiverAsDraft } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"

export const SignDocumentModal = ({
  totalDocumentCount,
  uuid,
  status,
  waiverSubmissionId,
  pendingSubmission,
  waiver,
  open,
  onCancel,
  onDraftSaved,
  onNext,
  bookingWaivers,
  fullScreen,
  showPageHeader
}) => {
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan
  const { t } = useTranslation()
  const { loading: submitting } = useSelector((state) => state.digitalWaiver)
  const [isChecked, setIsChecked] = useState(false)
  const [fileList, setFileList] = useState([])
  const [pdfUrls, setPdfUrls] = useState([])
  const [currentWaiverIndex, setCurrentWaiverIndex] = useState(0)
  const [signatureDataUrl, setSignatureDataUrl] = useState(null)
  const [documentStates, setDocumentStates] = useState([]) // Store checkbox and signature for each document
  const [signatureDisabled, setSignatureDisabled] = useState(false) // Control signature canvas interaction
  const [isNavigating, setIsNavigating] = useState(false) // Track when user is navigating between documents
  const [showDraftConfirmModal, setShowDraftConfirmModal] = useState(false)
  const [draftSubmitting, setDraftSubmitting] = useState(false)
  const signCanvas = useRef(null)
  const handleSignatureEnd = () => {
    // Prevent signature if document is submitted
    if (signatureDisabled) return

    const pad = signCanvas.current
    if (!pad || typeof pad.isEmpty !== "function" || pad.isEmpty() || typeof pad.getTrimmedCanvas !== "function") {
      setSignatureDataUrl(null)
      // Update document states
      setDocumentStates((prev) => {
        const newStates = [...prev]
        if (newStates[currentWaiverIndex]) {
          newStates[currentWaiverIndex] = {
            ...newStates[currentWaiverIndex],
            signatureDataUrl: null
          }
        }
        return newStates
      })
      return
    }
    const dataUrl = pad.toDataURL()
    setSignatureDataUrl(dataUrl)
    // Update document states
    setDocumentStates((prev) => {
      const newStates = [...prev]
      if (newStates[currentWaiverIndex]) {
        newStates[currentWaiverIndex] = {
          ...newStates[currentWaiverIndex],
          signatureDataUrl: dataUrl
        }
      }
      return newStates
    })
  }

  useEffect(() => {
    const sharedWaiver = status === "partially_signed" ? waiver?.pending_submissions : waiver?.waivers
    const tripBookingWaivers = pendingSubmission?.length > 0 ? pendingSubmission : bookingWaivers
    const waivers = sharedWaiver || tripBookingWaivers || []
    if (waivers.length > 0) {
      const fileObjects = waivers.map((waiverItem, index) => {
        const filename = waiverItem?.document_filename || waiverItem?.document_url?.split("/").pop() || `Document ${index + 1}`
        return {
          uid: `existing-${Date.now()}-${index}`,
          name: decodeURIComponent(filename),
          url: waiverItem.document_url,
          status: "done"
        }
      })
      setFileList(fileObjects)

      let createdUrls = []
      const fetchDocuments = async () => {
        try {
          const blobUrls = await Promise.all(
            waivers.map(async (w) => {
              const response = await fetch(w.document_url)
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
              const blob = await response.blob()
              return URL.createObjectURL(blob)
            })
          )
          createdUrls = blobUrls
          setPdfUrls(blobUrls)
        } catch (error) {
          console.error("Error fetching documents:", error)
          setPdfUrls(waivers.map((w) => w.document_url))
        }
      }
      fetchDocuments()

      return () => {
        createdUrls.forEach((url) => URL.revokeObjectURL(url))
      }
    }
  }, [waiver?.waivers, bookingWaivers, pendingSubmission])

  // Initialize document states when waivers are loaded
  useEffect(() => {
    if (pdfUrls.length > 0) {
      const newStates = Array(pdfUrls.length)
        .fill(null)
        .map(() => ({
          isChecked: false,
          signatureDataUrl: null,
          submitted: false
        }))
      setDocumentStates(newStates)
    }
  }, [pdfUrls.length])

  // Update current checkbox and signature when index changes
  useEffect(() => {
    if (documentStates[currentWaiverIndex]) {
      setIsChecked(documentStates[currentWaiverIndex].isChecked)
      
      // Only restore signature if document is submitted
      if (documentStates[currentWaiverIndex]?.submitted) {
        setSignatureDataUrl(documentStates[currentWaiverIndex].signatureDataUrl)
      } else if (isNavigating) {
        // Clear signature only when navigating to unsigned documents
        setSignatureDataUrl(null)
        setIsNavigating(false) // Reset navigation flag
      }

      // Only disable signature if document is already submitted
      const shouldDisable = documentStates[currentWaiverIndex]?.submitted
      setSignatureDisabled(shouldDisable)
    }
  }, [currentWaiverIndex, documentStates])

  // Restore signature on canvas when signatureDataUrl changes
  useEffect(() => {
    if (signCanvas.current) {
      const canvas = signCanvas.current
      // Clear canvas first to prevent overlapping signatures
      if (typeof canvas.clear === "function") {
        canvas.clear()
      }
      // Only restore if there's signature data
      if (signatureDataUrl && typeof canvas.fromDataURL === "function") {
        canvas.fromDataURL(signatureDataUrl)
      }

      // Apply disabled state to canvas element
      const canvasElement = canvas.getCanvas ? canvas.getCanvas() : null
      if (canvasElement) {
        if (signatureDisabled) {
          canvasElement.style.pointerEvents = "none"
          canvasElement.style.opacity = "0.5"
          canvasElement.style.cursor = "not-allowed"
        } else {
          canvasElement.style.pointerEvents = "auto"
          canvasElement.style.opacity = "1"
          canvasElement.style.cursor = "crosshair"
        }
      }
    }
  }, [signatureDataUrl, signatureDisabled])

  const clearSignature = () => {
    if (signCanvas.current && typeof signCanvas.current.clear === "function") {
      signCanvas.current.clear()
    }
    setSignatureDataUrl(null)
    // Update document states
    setDocumentStates((prev) => {
      const newStates = [...prev]
      if (newStates[currentWaiverIndex]) {
        newStates[currentWaiverIndex] = {
          ...newStates[currentWaiverIndex],
          signatureDataUrl: null
        }
      }
      return newStates
    })
  }
  const handleSaveDraft = () => {
    setShowDraftConfirmModal(true)
  }

  const handleDraftConfirmOk = async () => {
    setDraftSubmitting(true)
    try {
      await dispatch(saveWaiverAsDraft(waiverSubmissionId))
      setShowDraftConfirmModal(false)
      if (onDraftSaved) {
        onDraftSaved()
      } else {
        onCancel()
      }
    } finally {
      setDraftSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    // Check if current document has already been submitted
    if (documentStates[currentWaiverIndex]?.submitted) {
      // Already submitted, just move to next document
      if (currentWaiverIndex < pdfUrls.length - 1) {
        const nextIndex = currentWaiverIndex + 1
        setCurrentWaiverIndex(nextIndex)
        // Check if next document is submitted to determine if signature should be disabled
        if (documentStates[nextIndex]?.submitted) {
          setSignatureDisabled(true)
        } else {
          setSignatureDisabled(false)
          setIsNavigating(true) // Set navigation flag
          // Clear signature when moving to unsigned next document
          setSignatureDataUrl(null)
        }
      } else {
        // This was the last document, submission is complete
        if (onNext) {
          onNext()
        }
      }
      return
    }

    const formData = new FormData()

    // Always send the current document's state
    const sharedWaiver = status === "partially_signed" ? waiver?.pending_submissions : waiver?.waivers
    const tripBookingWaivers = pendingSubmission?.length > 0 ? pendingSubmission : bookingWaivers

    if (sharedWaiver || bookingWaivers) {
      const waivers = sharedWaiver || tripBookingWaivers || []
      const currentWaiverItem = waivers[currentWaiverIndex]
      if (currentWaiverItem) {
        formData.append(
          `waiver_submission[waiver_submission_documents_attributes][0][booking_waiver_id]`,
          currentWaiverItem.booking_waiver_id
        )

        // Add current document's signature if available
        if (documentStates[currentWaiverIndex]?.signatureDataUrl) {
          formData.append(
            `waiver_submission[waiver_submission_documents_attributes][0][signature]`,
            documentStates[currentWaiverIndex].signatureDataUrl
          )
        }
      }
    }

    // Send API call for current document - navigate to next only on success
    dispatch(updateWaiverSubmission(waiverSubmissionId, formData)).then((response) => {
      toaster.success(response?.message)

      // Mark current document as submitted
      setDocumentStates((prev) => {
        const newStates = [...prev]
        if (newStates[currentWaiverIndex]) {
          newStates[currentWaiverIndex] = {
            ...newStates[currentWaiverIndex],
            submitted: true
          }
        }
        return newStates
      })

      // Navigate to next document on success
      if (currentWaiverIndex < pdfUrls.length - 1) {
        const nextIndex = currentWaiverIndex + 1
        setCurrentWaiverIndex(nextIndex)
        setSignatureDisabled(false)
        setIsNavigating(true)
        setSignatureDataUrl(null)
      } else {
        // Last document done — resolve UUID from prop or API response
        const resolvedUuid =
          uuid ||
          response?.data?.trip_booking_uuid ||
          response?.data?.trip_booking?.uuid
        if (resolvedUuid) {
          dispatch(getWaiverSubmissionsByTripBooking(resolvedUuid))
        }
        if (onCancel) {
          onCancel(resolvedUuid)
        }
      }
    })
  }

  const content = (
    <>
      {/* Mandatory signing notice */}
      <Alert
        type="warning"
        showIcon
        message="Signature required"
        description="This waiver is mandatory. Please read the document carefully and sign it before your trip."
        style={{ marginBottom: 16 }}
      />

      {/* Document Preview Section */}
        <div className={styles.previewSection}>
          <div className={styles.title}>Document Preview</div>
          {pdfUrls.length > 0 && (
            <div className={styles.pdfPreviewContainer}>
              <div className={styles.pdfViewer}>
                <Flex align="center" justify="end">
                  {currentWaiverIndex + 1} of {pdfUrls.length} waivers
                </Flex>
                <div className={`mb-xs ${styles.documentTitle}`}>
                  Document {currentWaiverIndex + 1}: {fileList[currentWaiverIndex]?.name || `Document ${currentWaiverIndex + 1}`}
                </div>
                <object data={`${pdfUrls[currentWaiverIndex]}#navpanes=0`} type="application/pdf" className={styles.pdfObject}>
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    Unable to display PDF preview. Please download the file to view it.
                  </div>
                </object>
              </div>
            </div>
          )}

          {/* Legal Text & Checkbox */}
          <div className={`mt-xs ${styles.legalSection}`}>
            <Flex gap={12} align="start">
              <Checkbox
                checked={isChecked}
                onChange={(e) => {
                  const newChecked = e.target.checked
                  setIsChecked(newChecked)
                  // Update document states
                  setDocumentStates((prev) => {
                    const newStates = [...prev]
                    if (newStates[currentWaiverIndex]) {
                      newStates[currentWaiverIndex] = {
                        ...newStates[currentWaiverIndex],
                        isChecked: newChecked
                      }
                    }
                    return newStates
                  })
                }}
                className={`mt-xs ${styles.checkboxTop}`}
              />
              <div className={styles.legalText}>
                <div
                  onClick={() => {
                    const newChecked = !isChecked
                    setIsChecked(newChecked)
                    // Update document states
                    setDocumentStates((prev) => {
                      const newStates = [...prev]
                      if (newStates[currentWaiverIndex]) {
                        newStates[currentWaiverIndex] = {
                          ...newStates[currentWaiverIndex],
                          isChecked: newChecked
                        }
                      }
                      return newStates
                    })
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <p>{t("digital_waiver_and_contracts.leagal_documents")}</p>
                </div>
                <p>{t("digital_waiver_and_contracts.legal_document_desc")}</p>
              </div>
            </Flex>

            {/* Signature Section - Custom Styled */}
            {isChecked && (
              <div className={`mt-m relative ${styles.canvasLayer}`}>
                <div className={styles.title}>{t("digital_waiver_and_contracts.signature")}</div>
                <div className={`${styles.signatureCanvasWrapper} ${signatureDisabled ? styles.disabledCanvas : ""}`}>
                  {/* Actual Canvas Layer */}
                  <SignatureCanvas ref={signCanvas} penColor="black" onEnd={handleSignatureEnd} />
                  <div className={styles.signaturePlaceholderText}>{t("digital_waiver_and_contracts.draw_signature")}</div>
                  {/* Disabled overlay */}
                  {signatureDisabled && <div className={styles.disabledOverlay} />}
                </div>
                <Button
                  type="text"
                  size="small"
                  onClick={clearSignature}
                  className={styles.clearButton}
                  icon={<span style={{ fontSize: "20px" }}>×</span>}
                  disabled={signatureDisabled}
                ></Button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <Flex justify="flex-end" align="center">
          <Flex gap={12}>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={submitting || (isChecked && !!signatureDataUrl)}
            >
              Save Draft & Sign Later
            </Button>
            {waiver?.pending_submissions?.length === 1 || waiver?.waivers?.length === 1 ? null : (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  if (currentWaiverIndex > 0) {
                    const prevIndex = currentWaiverIndex - 1
                    setIsNavigating(true) // Set navigation flag
                    setCurrentWaiverIndex(prevIndex)
                  }
                }}
                disabled={currentWaiverIndex === 0}
              >
                {t("embedded.booking_confirmation.previous")}
              </Button>
            )}

            <Button
              color="primary"
              variant="solid"
              loading={submitting}
              onClick={handleSubmit}
              disabled={!isChecked || !signatureDataUrl || submitting}
            >
              {currentWaiverIndex < pdfUrls.length - 1 ? t("embedded.booking_confirmation.next") : "Submit"}
            </Button>
          </Flex>
        </Flex>
    </>
  )

  const draftConfirmModal = (
    <Modal
      open={showDraftConfirmModal}
      onCancel={() => setShowDraftConfirmModal(false)}
      footer={null}
      centered
      width={460}
      maskClosable={false}
    >
      <Flex vertical align="center" gap={24} style={{ padding: "16px 0 8px" }}>
        <ClockCircleOutlined style={{ fontSize: 56, color: "var(--color-primary)" }} />
        <Flex vertical align="center" gap={8}>
          <span style={{ fontSize: 22, fontWeight: 700, color: "var(--color-text-primary)" }}>Save Draft & Sign Later?</span>
          <span style={{ fontSize: 14, color: "var(--color-text-secondary-dark)", textAlign: "center" }}>
            Your progress will be saved as a draft. You can come back and finish signing the remaining documents whenever you&apos;re ready.
          </span>
          <span style={{ fontSize: 13, color: "var(--color-text-secondary-dark)", textAlign: "center", marginTop: 4 }}>
            To resume, you will receive a link via your email or phone number — you can continue signing from that link.
          </span>
        </Flex>
        <Flex gap={12} style={{ width: "100%" }}>
          <Button
            block
            onClick={() => setShowDraftConfirmModal(false)}
            disabled={draftSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            block
            loading={draftSubmitting}
            onClick={handleDraftConfirmOk}
          >
            Ok
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )

  if (fullScreen) {
    return (
      <>
        <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5", overflowY: "auto" }}>
          {showPageHeader && (
            <div style={{ width: "100%", backgroundColor: "#fff", padding: "12px 24px", borderBottom: "1px solid #f0f0f0", marginBottom: "16px" }}>
              <img src={logoSrc} alt="Digital Sportsman" style={{ height: "32px", width: "auto" }} />
            </div>
          )}
          <div style={{ maxWidth: "1600px", margin: "0 auto", padding: "0 16px 16px" }}>
            <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, color: "#272A34", fontSize: "18px", marginBottom: "16px" }}>
              Sign the document.
            </div>
            <div className={styles.fullScreenContent}>
              {content}
            </div>
          </div>
        </div>
        {draftConfirmModal}
      </>
    )
  }

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        centered
        width={1100}
        maskClosable={false}
        title={
          <span className={styles.modalTitle} style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, color: "#272A34", fontSize: "18px" }}>
            Sign the document.
          </span>
        }
      >
        {content}
      </Modal>
      {draftConfirmModal}
    </>
  )
}

SignDocumentModal.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  initialData: PropTypes.object,
  status: PropTypes.string
}
